import { supabase, isConfigured } from './supabase';
import type { AgentWidget, BrandConfig } from '../types';

/**
 * Fetch the signed-in agent's own widget (or null if they haven't
 * created one yet — first-login agents land on the onboarding form).
 */
export async function getMyWidget(agentId: string): Promise<AgentWidget | null> {
  if (!isConfigured) return null;
  try {
    const { data, error } = await supabase
      .from('valmate_agent_widgets')
      .select('*')
      .eq('agent_id', agentId)
      .maybeSingle();
    if (error) {
      console.warn('getMyWidget error', error);
      return null;
    }
    return (data ?? null) as AgentWidget | null;
  } catch (err) {
    console.warn('getMyWidget threw', err);
    return null;
  }
}

/**
 * Public anon-callable lookup for the branded landing page.
 * Returns null if the slug doesn't exist or its widget is paused/archived.
 */
export async function getBrandConfig(slug: string): Promise<BrandConfig | null> {
  if (!isConfigured) return null;
  try {
    const { data, error } = await supabase.rpc('valmate_widget_for_slug', {
      p_slug: slug,
    });
    if (error) {
      console.warn('valmate_widget_for_slug error', error);
      return null;
    }
    const result = data as { available: boolean } & BrandConfig;
    if (!result?.available) return null;
    return result;
  } catch (err) {
    console.warn('valmate_widget_for_slug threw', err);
    return null;
  }
}

/**
 * Create or update the signed-in agent's widget. Idempotent on
 * agent_id (which has a UNIQUE constraint).
 */
export async function upsertMyWidget(
  agentId: string,
  patch: Partial<Omit<AgentWidget, 'id' | 'agent_id' | 'created_at' | 'updated_at'>>
): Promise<{ success: boolean; error?: string; widget?: AgentWidget }> {
  if (!isConfigured) return { success: false, error: 'Supabase not configured.' };
  try {
    const { data, error } = await supabase
      .from('valmate_agent_widgets')
      .upsert(
        { agent_id: agentId, ...patch },
        { onConflict: 'agent_id' }
      )
      .select()
      .single();
    if (error) {
      console.warn('upsertMyWidget error', error);
      return { success: false, error: error.message };
    }
    return { success: true, widget: data as AgentWidget };
  } catch (err: any) {
    console.warn('upsertMyWidget threw', err);
    return { success: false, error: err?.message ?? 'Unknown error' };
  }
}

/**
 * Server-side check that a slug isn't taken (or is taken by us).
 * Used live as the agent types in the dashboard.
 */
export async function isSlugAvailable(slug: string): Promise<boolean> {
  if (!isConfigured) return false;
  try {
    const { data, error } = await supabase.rpc('valmate_slug_available', {
      p_slug: slug,
    });
    if (error) {
      console.warn('valmate_slug_available error', error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.warn('valmate_slug_available threw', err);
    return false;
  }
}

/**
 * Suggest a slug from a brand name. Lowercase, alphanumerics + dashes,
 * collapses repeats, trims to 32 chars, ensures it ends on alphanumerics.
 */
export function slugify(brandName: string): string {
  const base = brandName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '');
  if (base.length < 3) return base + '-co';
  return base;
}

/**
 * Build the public branded URL for a given slug. Uses VITE_VALMATE_BASE_URL
 * when set, otherwise the current origin.
 */
export function publicUrlForSlug(slug: string): string {
  const envBase = (import.meta as any).env?.VITE_VALMATE_BASE_URL as
    | string
    | undefined;
  const base =
    envBase && envBase.startsWith('http')
      ? envBase
      : typeof window !== 'undefined'
      ? window.location.origin
      : '';
  return `${base.replace(/\/$/, '')}/v/${slug}`;
}
